import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.notifications = [];
    this.listeners = new Map();
    this.continuousAudio = null;
    this.continuousNotificationInterval = null;
    this.openTicketsForUser = [];
    this.currentUserId = null;
    this.openTicketsInterval = null;
    this.isMuted = JSON.parse(
      localStorage.getItem('notificationsMuted') || 'false'
    );
  }

  // Conectar ao servidor WebSocket
  connect(userId) {
    // Idempotência: não reconecta se já está conectado com o mesmo usuário
    if (this.socket && this.currentUserId === userId) {
      return this.socket;
    }

    if (this.socket) {
      this.disconnect();
    }

    // Armazenar o ID do usuário
    this.currentUserId = userId;

    const serverUrl = 'https://os.opennew.com.br';

    this.socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      console.log('Conectado ao servidor WebSocket');
      this.isConnected = true;

      if (userId) {
        this.socket.emit('authenticate', userId);
        this.updateOpenTicketsForUser();
        this.updatePreTicketsForUser();

        // Limpa intervalos anteriores antes de criar novos — o evento 'connect'
        // é disparado a cada reconexão automática do socket.io, e sem essa limpeza
        // cada reconexão acumula um novo intervalo paralelo (memory/CPU leak).
        if (this.preTicketsInterval) {
          clearInterval(this.preTicketsInterval);
        }
        if (this.openTicketsInterval) {
          clearInterval(this.openTicketsInterval);
        }

        this.preTicketsInterval = setInterval(() => {
          this.updatePreTicketsForUser();
        }, 30000);

        this.openTicketsInterval = setInterval(() => {
          this.updateOpenTicketsForUser();
        }, 30000);
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Desconectado do servidor WebSocket');
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('Erro de conexão WebSocket:', error);
      this.isConnected = false;
    });

    // Escutar notificações
    this.socket.on('notification', (notification) => {
      console.log('Nova notificação recebida:', notification);
      this.handleNotification(notification);
    });

    return this.socket;
  }

  // Desconectar do servidor WebSocket
  disconnect() {
    // Parar notificações contínuas
    this.stopContinuousNotification();

    // Limpar intervalo de verificação de pré-tickets
    if (this.preTicketsInterval) {
      clearInterval(this.preTicketsInterval);
      this.preTicketsInterval = null;
    }

    // Limpar intervalo de verificação de tickets em aberto
    if (this.openTicketsInterval) {
      clearInterval(this.openTicketsInterval);
      this.openTicketsInterval = null;
    }

    // Limpar todos os event listeners
    this.listeners.clear();

    if (this.socket) {
      // Remover todos os listeners do socket antes de desconectar
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }

    // Limpar dados do usuário
    this.currentUserId = null;
    this.openTicketsForUser = [];
    this.preTicketsCount = 0;
    this.notifications = [];
  }

  // Processar notificação recebida
  handleNotification(notification) {
    // Adicionar à lista de notificações
    this.notifications.unshift(notification);

    // Limitar a 50 notificações
    if (this.notifications.length > 50) {
      this.notifications = this.notifications.slice(0, 50);
    }

    // Mostrar notificação do navegador se permitido
    this.showBrowserNotification(notification);

    // Processar som baseado no tipo de notificação
    this.handleNotificationSound(notification);

    // Notificar listeners
    this.notifyListeners('notification', notification);
  }

  setMuted(muted) {
    this.isMuted = !!muted;
    localStorage.setItem('notificationsMuted', JSON.stringify(this.isMuted));
    if (this.isMuted) {
      this.stopContinuousNotification();
    }
    this.notifyListeners('notificationMuteUpdate', { muted: this.isMuted });
  }

  getMuted() {
    return !!this.isMuted;
  }

  // Mostrar notificação do navegador
  // eslint-disable-next-line class-methods-use-this
  showBrowserNotification(notification) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id,
      });

      // Fechar automaticamente após 5 segundos
      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    }
  }

  // Processar som baseado no tipo de notificação
  handleNotificationSound(notification) {
    if (notification.type === 'ticket_assignment') {
      // Tocar som uma vez para atribuição
      this.playNotificationSoundOnce();
      // Atualizar lista de tickets em aberto para o usuário
      this.updateOpenTicketsForUser();
    } else if (notification.type === 'ticket_status_change') {
      const ns = notification?.data?.newStatus || notification?.data?.status;
      if (ns) {
        const lower = String(ns).toLowerCase();
        if (lower !== 'aberto') {
          this.stopContinuousNotification();
        }
      }
      this.updateOpenTicketsForUser();
    }
  }

  // Tocar som de notificação uma vez
  // eslint-disable-next-line class-methods-use-this
  playNotificationSoundOnce() {
    if (this.isMuted) {
      return;
    }
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.7;
      audio.play().catch((error) => {
        console.log('Não foi possível reproduzir o som de notificação:', error);
      });
    } catch (error) {
      console.log('Som de notificação não disponível');
    }
  }

  // Iniciar notificação contínua para tickets em aberto
  startContinuousNotification() {
    if (this.continuousNotificationInterval) {
      return; // Já está tocando
    }
    if (this.isMuted) {
      return;
    }

    this.continuousNotificationInterval = setInterval(() => {
      // Filtrar apenas tickets com status 'aberto' para notificação sonora
      const ticketsAbertos = this.openTicketsForUser.filter(
        (ticket) => ticket.status === 'aberto'
      );

      if (ticketsAbertos.length > 0) {
        if (!this.isMuted) {
          this.playNotificationSoundOnce();
        }
      } else {
        this.stopContinuousNotification();
      }
    }, 30000); // Tocar a cada 30 segundos
  }

  // Parar notificação contínua
  stopContinuousNotification() {
    if (this.continuousNotificationInterval) {
      clearInterval(this.continuousNotificationInterval);
      this.continuousNotificationInterval = null;
    }
  }

  // Atualizar lista de tickets em aberto para o usuário
  async updateOpenTicketsForUser() {
    if (!this.currentUserId) {
      console.warn('ID do usuário não definido para verificar tickets');
      return;
    }

    try {
      const token = this.getAuthToken();
      if (!token) {
        console.warn('Token de autenticação não encontrado');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/tickets/open-for-user/${this.currentUserId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const openTickets = await response.json();
      this.openTicketsForUser = openTickets;

      console.log(
        `Tickets em aberto para usuário ${this.currentUserId}:`,
        openTickets.length
      );

      // Criar notificações para tickets em aberto
      openTickets.forEach((ticket) => {
        const existingNotification = this.notifications.find(
          (n) =>
            n.data && n.data.ticketId === ticket.id && n.type === 'ticket_open'
        );

        if (!existingNotification) {
          const notification = {
            id: `ticket_open_${ticket.id}_${Date.now()}`,
            type: 'ticket_open',
            title: 'Ticket em Aberto',
            message: `Ticket #${ticket.id}: ${ticket.title}`,
            timestamp: Date.now(),
            read: false,
            data: {
              ticketId: ticket.id,
              status: ticket.status,
            },
          };

          // Adicionar à lista de notificações sem processar som
          this.notifications.unshift(notification);

          // Limitar a 50 notificações
          if (this.notifications.length > 50) {
            this.notifications = this.notifications.slice(0, 50);
          }
        }
      });

      // Filtrar apenas tickets com status 'aberto' para notificação sonora
      const openTicketsOnly = openTickets.filter(
        (ticket) => ticket.status === 'aberto'
      );

      // Iniciar notificação contínua se houver tickets 'aberto'
      if (openTicketsOnly.length > 0) {
        this.startContinuousNotification();
      } else {
        this.stopContinuousNotification();
      }

      // Notificar listeners sobre mudanças nas notificações
      this.notifyListeners('notification', null);

      // Atualizar contagem para o ícone de notificação
      this.updateNotificationIcon();
    } catch (error) {
      console.error('Erro ao buscar tickets em aberto:', error);
    }
  }

  // Verificar pré-tickets para notificações
  async updatePreTicketsForUser() {
    try {
      const token = this.getAuthToken();
      if (!token) {
        console.warn('Token de autenticação não encontrado para pré-tickets');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/pretickets`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const preTickets = await response.json();
      const previousCount = this.preTicketsCount || 0;
      this.preTicketsCount = preTickets.length;

      console.log(`Pré-tickets encontrados:`, preTickets.length);

      // Criar notificações para todos os pré-tickets
      preTickets.forEach((preTicket) => {
        const existingNotification = this.notifications.find(
          (n) =>
            n.data &&
            n.data.preTicketId === preTicket.id &&
            n.type === 'preticket_item'
        );

        if (!existingNotification) {
          const notification = {
            id: `preticket_item_${preTicket.id}_${Date.now()}`,
            type: 'preticket_item',
            title: 'Pré-ticket Pendente',
            message: `Pré-ticket: ${preTicket.subject || preTicket.title || 'Sem título'}`,
            timestamp: Date.now(),
            read: false,
            data: {
              preTicketId: preTicket.id,
            },
          };

          // Adicionar à lista de notificações sem processar som
          this.notifications.unshift(notification);

          // Limitar a 50 notificações
          if (this.notifications.length > 50) {
            this.notifications = this.notifications.slice(0, 50);
          }
        }
      });

      // Se há novos pré-tickets, criar notificação de resumo
      if (preTickets.length > previousCount) {
        const newPreTicketsCount = preTickets.length - previousCount;
        const notification = {
          id: `preticket_new_${Date.now()}`,
          type: 'preticket_new',
          title: 'Novos Pré-tickets',
          message: `${newPreTicketsCount} novo(s) pré-ticket(s) recebido(s)`,
          timestamp: Date.now(),
          read: false,
          data: {
            count: newPreTicketsCount,
            total: preTickets.length,
          },
        };

        this.handleNotification(notification);
      }

      // Notificar listeners sobre mudanças nas notificações
      this.notifyListeners('notification', null);

      // Atualizar contagem para o ícone de notificação
      this.updateNotificationIcon();
    } catch (error) {
      console.error('Erro ao buscar pré-tickets:', error);
    }
  }

  // Atualizar ícone de notificação com contagem total
  updateNotificationIcon() {
    const totalTickets =
      (this.openTicketsForUser || []).filter(
        (ticket) => ticket.status === 'aberto'
      ).length || 0;
    const totalPreTickets = this.preTicketsCount || 0;
    const totalNotifications = totalTickets + totalPreTickets;

    // Notificar listeners sobre mudança na contagem
    this.notifyListeners('notificationCountUpdate', {
      tickets: totalTickets,
      preTickets: totalPreTickets,
      total: totalNotifications,
    });
  }

  // Solicitar permissão para notificações do navegador
  // eslint-disable-next-line class-methods-use-this
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  // Adicionar listener para eventos
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }

    // Verificar se o callback já existe para evitar duplicatas
    const callbacks = this.listeners.get(event);
    if (!callbacks.includes(callback)) {
      callbacks.push(callback);
    }
  }

  // Remover listener
  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);

        // Limpar array vazio para economizar memória
        if (callbacks.length === 0) {
          this.listeners.delete(event);
        }
      }
    }
  }

  // Notificar listeners com tratamento robusto de erros
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);

      // Criar cópia do array para evitar problemas se callbacks forem removidos durante iteração
      const callbacksCopy = [...callbacks];

      callbacksCopy.forEach((callback) => {
        try {
          // Verificar se o callback ainda é válido
          if (typeof callback === 'function') {
            // Usar timeout para evitar bloqueio em callbacks lentos
            setTimeout(() => {
              try {
                callback(data);
              } catch (error) {
                console.error('Erro ao executar listener assíncrono:', error);
                // Remover callback problemático
                this.removeEventListener(event, callback);
              }
            }, 0);
          }
        } catch (error) {
          console.error('Erro ao executar listener:', error);
          // Remover callback problemático
          this.removeEventListener(event, callback);
        }
      });
    }
  }

  // Obter todas as notificações
  getNotifications() {
    return this.notifications;
  }

  // Obter notificações não lidas
  getUnreadNotifications() {
    return this.notifications.filter((n) => !n.read);
  }

  // Marcar notificação como lida
  markAsRead(notificationId) {
    const notification = this.notifications.find(
      (n) => n.id === notificationId
    );
    if (notification) {
      const updatedNotification = { ...notification, read: true };
      const index = this.notifications.findIndex(
        (n) => n.id === notificationId
      );
      this.notifications[index] = updatedNotification;
      this.notifyListeners('notificationRead', updatedNotification);
    }
  }

  // Marcar todas como lidas
  markAllAsRead() {
    this.notifications = this.notifications.map((notification) => ({
      ...notification,
      read: true,
    }));
    this.notifyListeners('allNotificationsRead');
  }

  // Limpar todas as notificações
  clearNotifications() {
    this.notifications = [];
    this.notifyListeners('notificationsCleared');
  }

  // Verificar se está conectado
  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected;
  }

  // Obter contagem de notificações não lidas
  getUnreadCount() {
    return this.notifications.filter((n) => !n.read).length;
  }

  // Obter token de autenticação do Redux store
  // eslint-disable-next-line class-methods-use-this
  getAuthToken() {
    try {
      // Tentar obter do Redux store primeiro
      // eslint-disable-next-line no-underscore-dangle
      if (window.__REDUX_STORE__) {
        // eslint-disable-next-line no-underscore-dangle
        const state = window.__REDUX_STORE__.getState();
        if (state.auth && state.auth.token) {
          return state.auth.token;
        }
      }

      // Fallback: tentar obter do localStorage (se persistido)
      const persistedState = localStorage.getItem('persist:auth');
      if (persistedState) {
        const authState = JSON.parse(persistedState);
        if (authState.token) {
          return JSON.parse(authState.token);
        }
      }

      return null;
    } catch (error) {
      console.error('Erro ao obter token de autenticação:', error);
      return null;
    }
  }
}

// Exportar instância única
export default new WebSocketService();
